import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUserProfile, type UserProfile, clearUserProfileCache } from '../services/graphql/userService';
import { subscribeToAuthState, getCurrentUser } from '../services/graphql/authService';
import logger from '../services/logger';

export interface RoleContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType>({
  userProfile: null,
  loading: true,
  error: null,
  refreshProfile: async () => {},
});

export const useRoleContext = () => useContext(RoleContext);

interface RoleProviderProps {
  children: ReactNode;
}

/**
 * RoleProvider fetches and provides the current user's profile and role info.
 * It logs all state changes for debugging.
 */
export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      logger.debug('Fetching current user profile');
      const { data, error: fetchError } = await getCurrentUserProfile();
      if (fetchError) {
        // If the error is just 'No active session', treat as not logged in, not a real error
        if (fetchError.message === 'No active session. User is not logged in.') {
          setUserProfile(null);
          setError(null);
          logger.debug('No active session');
        } else {
          setError(fetchError.message || 'Failed to fetch user profile');
          setUserProfile(null);
          logger.error('Error fetching user profile', fetchError);
        }
      } else {
        setUserProfile(data);
        setError(null);
        logger.info('User profile loaded', { userId: data?.id, role: data?.role });
      }
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      setUserProfile(null);
      logger.error('Exception fetching user profile', err);
    } finally {
      setLoading(false);
      logger.debug('Fetch profile finished', { userProfileId: userProfile?.id ?? null, error });
    }
  };

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          // User is authenticated, fetch profile
          await fetchProfile();
        } else {
          // No authenticated user
          setUserProfile(null);
          setLoading(false);
          setError(null);
          logger.debug('No authenticated user on initial load');
        }
      } catch {
        // No authenticated user
        setUserProfile(null);
        setLoading(false);
        setError(null);
        logger.debug('No authenticated user on initial load');
      }
    };

    checkAuth();

    // Subscribe to auth state changes using GraphQL auth service
    const unsubscribe = subscribeToAuthState((user) => {
      logger.info('Auth state changed', { status: user ? 'logged in' : 'logged out' });

      if (user) {
        // User signed in, fetch profile
        logger.debug('User signed in, fetching profile');
        clearUserProfileCache();
        fetchProfile();
      } else {
        // User signed out
        setUserProfile(null);
        setLoading(false);
        setError(null);
        clearUserProfileCache();
        logger.info('User signed out');
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <RoleContext.Provider value={{ userProfile, loading, error, refreshProfile: fetchProfile }}>
      {children}
    </RoleContext.Provider>
  );
};
